import { z } from "zod";
import { youtubeLinkField } from "@/core/lib/youtube";
import { Days } from "@/core/types/utils";

export const roomsSearchSchema = z.object({
	search: z.string().catch(""),
	page: z.number().catch(1),
	pageSize: z.number().catch(10),
	sortBy: z.enum(["code", "shortTitle", "longTitle", "roomCode"]).catch("code"),
	orderBy: z.enum(["asc", "desc"]).catch("desc"),
});

export const incentivesDataSchema = z.object({
	title: z.string().min(1).max(20),
	description: z.string().min(1).max(100),
	index: z.transform(Number).pipe(z.number().int()),
});
export type incentivesDataValues = z.output<typeof incentivesDataSchema>;

export type roomSearch = z.output<typeof roomsSearchSchema>;

export enum AccommodationType {
	Room = "Room",
	Apartment = "Apartment",
	House = "House",
	Lodge = "Lodge",
	Villa = "Villa",
	Chalet = "Chalet",
	Cabin = "Cabin",
	Suite = "Suite",
	Penthouse = "Penthouse",
	Studio = "Studio",
}

export enum PricingType {
	"Not applicable" = "Not applicable",
	"1 A" = "1",
	"2 A" = "2",
	"3 A" = "3",
	"4 A" = "4",
	"5 A" = "5",
	"6 A" = "6",
	"7 A" = "7",
	"8 A" = "8",
	"9 A" = "9",
}

export const ROOM_FORM_TABS = {
	roomDetails: "generalInfo",
	facilitiesDetails: "facilities",
	advancedDetails: "advanced",
	checkInCheckOutDetails: "checkingCheckout",
};

export const MAX_ROOM_INCENTIVES = 3;

export const roomDetailsFormBaseSchema = z.object({
	shortTitle: z
		.string({ error: "val.required" })
		.trim()
		.min(1, { error: "val.required" })
		.max(30, { error: "val.max30" }),
	longTitle: z
		.string({ error: "val.required" })
		.min(1, { error: "val.required" })
		.max(50, { error: "val.max50" })
		.trim(),
	shortDescription: z
		.string({ error: "val.required" })
		.min(1, { error: "val.required" })
		.max(255, { error: "val.max255" })
		.trim(),
	description: z
		.string({ error: "val.required" })
		.min(1, { error: "val.required" })
		.max(500, { error: "val.max500" })
		.trim(),
	roomCode: z.string().min(1).max(60, { error: "val.max60" }).trim(),
	minAdults: z
		.transform(Number)
		.pipe(z.number().int().min(1, { error: "val.min1" })),
	maxAdults: z
		.transform(Number)
		.pipe(z.number().int().min(1, { error: "val.min1" })),
	maxOccupants: z
		.transform(Number)
		.pipe(z.number().int().min(1, { error: "val.min1" })),
	children: z
		.transform(Number)
		.pipe(z.number().int().min(0, { error: "val.min0" })),
	single: z.transform(Number).pipe(z.number().int()),
	double: z.transform(Number).pipe(z.number().int()),
	queen: z.transform(Number).pipe(z.number().int()),
	king: z.transform(Number).pipe(z.number().int()),
	superKing: z.transform(Number).pipe(z.number().int()),
	sofaBed: z.transform(Number).pipe(z.number().int()),
	bunkBed: z.transform(Number).pipe(z.number().int()),
	bedrooms: z.transform(Number).pipe(z.number().int()),
	accommodationType: z.enum(AccommodationType),
	availableRooms: z
		.transform(Number)
		.pipe(z.number().int().min(1, { error: "val.min1" })),
	images: z
		.array(z.string().min(1, { error: "val.required" }))
		.min(1, { error: "val.required" }),
	youtubeVideoId: youtubeLinkField,
	incentives: z.array(incentivesDataSchema).max(MAX_ROOM_INCENTIVES),
});

export const roomDetailsFormSchemaWithRefine = roomDetailsFormBaseSchema.refine(
	(data) => {
		return (
			data.single > 0 ||
			data.double > 0 ||
			data.queen > 0 ||
			data.king > 0 ||
			data.superKing > 0 ||
			data.sofaBed > 0 ||
			data.bunkBed > 0
		);
	},
	{
		message: "At least one bed type must be selected",
		path: ["single"],
	},
);
export type roomDetailsFormValues = z.output<typeof roomDetailsFormBaseSchema>;

export const roomFacilitiesFormSchema = z.object({
	accessibleRoom: z.boolean(),
	facilities: z.array(z.string()),
});

export type roomFacilitiesFormValues = z.output<
	typeof roomFacilitiesFormSchema
>;
export type roomFacilitiesFormInputValues = z.input<
	typeof roomFacilitiesFormSchema
>;

export const roomAdvancedFormSchema = z.object({
	pricing: z.enum(PricingType),
});

export type roomAdvancedFormValues = z.output<typeof roomAdvancedFormSchema>;
export type roomAdvancedFormInputValues = z.input<
	typeof roomAdvancedFormSchema
>;

export const checkInOutFormSchema = z.object({
	day: z.enum(Days),
	time: z.string(),
	allocation: z
		.transform(Number)
		.pipe(z.number().int().min(0, { error: "val.min0" })),
	price: z.transform(Number).pipe(z.number().min(0, { error: "val.min0" })),
});

export const roomsCheckInOutFormSchema = z.object({
	earlyCheckIn: z.array(checkInOutFormSchema),
	lateCheckOut: z.array(checkInOutFormSchema),
	exceptions: z.array(z.string()).optional(),
});

export type checkInOutFormValues = z.output<typeof checkInOutFormSchema>;
export type checkInOutFormInputValues = z.input<typeof checkInOutFormSchema>;

export const roomFormSchema = z.object({
	roomDetails: roomDetailsFormSchemaWithRefine,
	facilitiesDetails: roomFacilitiesFormSchema,
	advancedDetails: roomAdvancedFormSchema,
	checkInCheckOutDetails: roomsCheckInOutFormSchema,
});

export type roomFormValues = z.output<typeof roomFormSchema>;
export type roomFormInputValues = z.input<typeof roomFormSchema>;

export const roomSchema = roomFormSchema.extend({
	roomDetails: roomDetailsFormBaseSchema.extend({
		id: z.string(),
	}),
});

export type Room = z.infer<typeof roomSchema>;
